/**
 * Non-UI model + helpers for the enroll flow. Despite the per-symbol focus on "steps", this file
 * also owns the selection/applicant state shapes, group/plan lookups, gating/validation, and
 * pricing helpers. Kept together as the enroll domain layer; split by concern if it keeps growing.
 */
import type {
  CoverageTier,
  GroupConfig,
  PlanConfig,
  ProductCategory,
  ProductConfig,
} from '@client/pages/public/enroll/types';
import {groupOffersProtection, requiresBeneficiary} from '@client/pages/public/enroll/types';
import type {
  Beneficiary,
  Dependent,
  PersonalInfo,
} from '@client/pages/public/enroll/checkout/checkoutSchema';
import {
  EMPTY_BENEFICIARY,
  EMPTY_PERSONAL_INFO,
  buildCheckoutSchema,
} from '@client/pages/public/enroll/checkout/checkoutSchema';

export const STEP = {
  Medical: 'medical',
  Dental: 'dental',
  Vision: 'vision',
  Protection: 'protection',
  Review: 'review',
  Checkout: 'checkout',
  Confirmation: 'confirmation',
} as const;

export type EnrollStep = (typeof STEP)[keyof typeof STEP];

export const STEP_LABELS: Record<EnrollStep, string> = {
  medical: 'Medical',
  dental: 'Dental',
  vision: 'Vision',
  protection: 'Extra Protection',
  review: 'Review',
  checkout: 'Checkout',
  confirmation: 'Confirmation',
};

/**
 * Intro copy for the Extra Protection step. Unlike product steps (which read their header from
 * the product config), protection aggregates several products and has no per-product home for
 * this. Shared across groups for now; promote to GroupConfig if a group needs custom copy.
 */
export const PROTECTION_BLURB =
  "Health insurance covers the medical bills. These optional plans cover the rest — a lump sum on a serious diagnosis, income protection if you can't work, and life insurance for your family. Add any, all, or none and continue.";

/** Per-category choice: a chosen plan, or an explicit decline. */
export interface Selection {
  planId?: string;
  declined?: boolean;
}

/** The primary enrollee's identity/contact/address info. Shape owned by the checkout schema. */
export type Applicant = PersonalInfo;
export type {Beneficiary, Dependent};

export const EMPTY_APPLICANT: Applicant = EMPTY_PERSONAL_INFO;
export {EMPTY_BENEFICIARY};

export type EnrollSelections = Partial<Record<ProductCategory, Selection>>;

/** Per-category coverage tier. Absent categories fall back to {@link DEFAULT_TIER}. */
export type EnrollTiers = Partial<Record<ProductCategory, CoverageTier>>;

export const DEFAULT_TIER: CoverageTier = 'employee';

export interface EnrollStepsState {
  tiers: EnrollTiers;
  selections: EnrollSelections;
  applicant: Applicant;
  dependents: Dependent[];
  beneficiary: Beneficiary;
}

/** `tiers` with employee-only products (coverage tiers off) back on the default, whatever was stored. */
export function effectiveTiers(group: GroupConfig, tiers: EnrollTiers): EnrollTiers {
  const result = {...tiers};
  for (const product of group.products) {
    if (product.tierSelectable === false) delete result[product.category];
  }
  return result;
}

/** The effective tier for a category, falling back to the employee-only default. */
export function tierFor(tiers: EnrollTiers, category: ProductCategory): CoverageTier {
  return tiers[category] ?? DEFAULT_TIER;
}

export const PRODUCT_CATEGORY_STEPS: ProductCategory[] = ['medical', 'dental', 'vision'];

/** Steps that gate on a chosen-or-declined product category. */
export function isProductStep(
  step: EnrollStep | ProductCategory,
): step is 'medical' | 'dental' | 'vision' {
  return step === STEP.Medical || step === STEP.Dental || step === STEP.Vision;
}

/**
 * The product a product step is built around. With no regular medical, limited medical is the
 * medical step (same route and stepper label).
 */
export function stepProduct(
  group: GroupConfig,
  step: 'medical' | 'dental' | 'vision',
): ProductConfig | undefined {
  const product = getProduct(group, step);
  if (product || step !== STEP.Medical) return product;
  return getProduct(group, 'limited-medical');
}

// Limited medical is opt-in unless it's given a decline prompt, so it only gates then.
function gatesStep(product: ProductConfig): boolean {
  return product.category !== 'limited-medical' || product.declinable !== false;
}

/**
 * Ordered step list for a group: each standalone product (medical/dental/vision),
 * then a single combined Extra Protection step (if offered), then review/checkout/confirmation.
 */
export function buildSteps(group: GroupConfig): EnrollStep[] {
  const steps: EnrollStep[] = [];
  for (const step of PRODUCT_CATEGORY_STEPS) {
    if (isProductStep(step) && stepProduct(group, step)) steps.push(step);
  }
  if (groupOffersProtection(group)) steps.push(STEP.Protection);
  steps.push(STEP.Review, STEP.Checkout, STEP.Confirmation);
  return steps;
}

/** Product categories that a given step gates on (empty for review/checkout/confirmation). */
export function stepCategories(group: GroupConfig, step: EnrollStep): ProductCategory[] {
  if (step === STEP.Protection) {
    return group.products.filter((p) => p.inlineWith === 'protection').map((p) => p.category);
  }
  if (isProductStep(step)) {
    const product = stepProduct(group, step);
    return product && gatesStep(product) ? [product.category] : [];
  }
  return [];
}

export function getProduct(
  group: GroupConfig,
  category: ProductCategory,
): ProductConfig | undefined {
  return group.products.find((p) => p.category === category);
}

export function getPlan(group: GroupConfig, planId: string): PlanConfig | undefined {
  return group.plans.find((p) => p.id === planId);
}

function isCategorySatisfied(
  group: GroupConfig,
  selections: EnrollSelections,
  category: ProductCategory,
): boolean {
  const selection = selections[category];
  if (!selection) return false;
  if (selection.declined) return true;
  return !!selection.planId;
}

/** Whether a chosen plan's product asks for a beneficiary. */
export function needsBeneficiary(group: GroupConfig, selections: EnrollSelections): boolean {
  const elected = Object.entries(selections)
    .filter(([, selection]) => selection?.planId)
    .map(([category]) => category);
  return requiresBeneficiary(group, elected);
}

/**
 * Full checkout-payload validity (applicant + every dependent + beneficiary when needed). Use
 * this — not applicant-only — to gate submission, so a restored session with a valid subscriber
 * but invalid dependent can't post an incomplete enrollment.
 */
export function isCheckoutValid(
  group: GroupConfig,
  state: Pick<EnrollStepsState, 'selections' | 'applicant' | 'dependents' | 'beneficiary'>,
): boolean {
  return buildCheckoutSchema({beneficiary: needsBeneficiary(group, state.selections)})
    .safeParse({
      ...state.applicant,
      dependents: state.dependents,
      beneficiary: state.beneficiary,
      smsConsent: false,
    })
    .success;
}

/** Label for the forward/primary button on a given step (submit on the final input step). */
export function continueButtonLabel(step: EnrollStep): string {
  return step === STEP.Checkout ? 'Submit enrollment' : 'Continue';
}

/**
 * Gated product-step categories (medical/dental/vision) the user has neither chosen nor declined.
 * These block Review/Checkout — a user can land here by editing back and clearing a prior choice.
 */
export function unresolvedProductSteps(
  group: GroupConfig,
  selections: EnrollSelections,
): ProductCategory[] {
  return PRODUCT_CATEGORY_STEPS.flatMap((step) =>
    isProductStep(step) ? stepCategories(group, step) : [],
  ).filter((category) => !isCategorySatisfied(group, selections, category));
}

/** Whether a step's requirements are met (drives Continue gating and tracker status). */
export function isStepComplete(group: GroupConfig, state: EnrollStepsState, step: EnrollStep): boolean {
  if (step === STEP.Confirmation) return true;
  // Extra Protection is opt-in ("add any, all, or none") — never gates Continue.
  if (step === STEP.Protection) return true;
  if (step === STEP.Review) return unresolvedProductSteps(group, state.selections).length === 0;
  if (step === STEP.Checkout) {
    return (
      unresolvedProductSteps(group, state.selections).length === 0 &&
      isCheckoutValid(group, state)
    );
  }
  return stepCategories(group, step).every((category) =>
    isCategorySatisfied(group, state.selections, category),
  );
}

const USD = new Intl.NumberFormat('en-US', {style: 'currency', currency: 'USD'});

export function formatUsd(amount: number): string {
  return USD.format(amount);
}

/** Weekly cost of all chosen plans, each priced at its own per-category tier. */
export function selectionTotal(
  group: GroupConfig,
  tiers: EnrollTiers,
  selections: EnrollSelections,
): number {
  let total = 0;
  for (const [category, selection] of Object.entries(selections) as [ProductCategory, Selection][]) {
    if (!selection || selection.declined || !selection.planId) continue;
    const plan = getPlan(group, selection.planId);
    if (plan) total += plan.rates[tierFor(tiers, category)];
  }
  return total;
}
